import { ArrowRight, GitBranch, List, Loader2, Network, Pencil, Trash2, X } from "lucide-react";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { GardenMetric, GraphLink, NoteCard, RelationType, SourceNode } from "../types";
import { LIFECYCLE_OPTIONS, defaultStatusForKind, statusColor, statusMeta } from "../lib/lifecycle";
import { useSecondClickConfirm } from "../lib/useSecondClickConfirm";
import { StatusBoard } from "./StatusBoard";

// Lazy-loaded so the heavier interactive physics canvas only ships when the
// user actually opens the Obsidian view.
const ObsidianForceGraph = lazy(() => import("./ObsidianForceGraph"));

type UpdateSource = (id: string, input: { status?: string }) => Promise<void> | void;

type Props = {
  metrics: GardenMetric[];
  links: GraphLink[];
  sources?: SourceNode[];
  notes?: NoteCard[];
  relationTypes?: RelationType[];
  relationError?: string;
  onUpdateSource?: UpdateSource;
  onUpdateRelation?: (id: string, input: { relationType: string }) => Promise<void>;
  onDeleteRelation?: (id: string) => Promise<void>;
  onOpenReviewQueue?: () => void;
  // When set, render only one view (used as separate Library sub-tabs): map=Obsidian graph,
  // list=relations list, kanban=status board. Hides the internal view switches.
  only?: "map" | "list" | "kanban";
};

type GraphNode = {
  id: string;
  title: string;
  label: string;
  content: string;
  status: string;
};

type NodePosition = {
  x: number;
  y: number;
};

const noteLikeLabels = new Set(["note", "task", "quote", "practice", "exercise", "reflection"]);

const DEFAULT_NODE_LIMIT = 30;
const NODE_LIMIT_PRESETS = [30, 60, 100];

function labelText(value?: string) {
  return value?.replace(/_/g, " ") || "object";
}

function relationText(link: GraphLink) {
  const label = link.labelName || link.label;
  return label.replace(/_/g, " ").toLowerCase();
}

function shortTitle(value: string, max = 44) {
  return value.length > max ? `${value.slice(0, max - 3)}...` : value;
}

function plainText(value?: string) {
  return (value ?? "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[#>*_`[\]()~-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isNoteLike(label?: string) {
  return noteLikeLabels.has(label ?? "");
}

function nodePrimaryText(node: GraphNode, max = 96) {
  const content = plainText(node.content);

  if (isNoteLike(node.label) && content) {
    return shortTitle(content, max);
  }

  return shortTitle(node.title, max);
}

function nodeSecondaryText(node: GraphNode) {
  const content = plainText(node.content);

  if (!isNoteLike(node.label) || !content) {
    return "";
  }

  return node.title;
}

function mergeNode(map: Map<string, GraphNode>, next: GraphNode) {
  const existing = map.get(next.id);

  if (!existing) {
    map.set(next.id, next);
    return;
  }

  map.set(next.id, {
    ...existing,
    title: existing.title || next.title,
    label: existing.label || next.label,
    content: existing.content || next.content,
    status: existing.status || next.status
  });
}

function nodeFromSource(source: SourceNode): GraphNode {
  return {
    id: source.id,
    title: source.title,
    label: labelText(source.kind),
    content: "",
    status: source.status ?? defaultStatusForKind(source.kind)
  };
}

function nodeFromNote(note: NoteCard): GraphNode {
  return {
    id: note.id,
    title: note.title,
    label: labelText(note.kind),
    content: note.content ?? "",
    status: note.status ?? defaultStatusForKind(note.kind)
  };
}

function nodeFromLink(link: GraphLink, side: "from" | "to"): GraphNode {
  return side === "from"
    ? {
        id: link.fromId ?? link.from,
        title: link.from,
        label: labelText(link.fromLabel),
        content: link.fromContent ?? "",
        status: link.fromStatus ?? defaultStatusForKind(link.fromLabel ?? "")
      }
    : {
        id: link.toId ?? link.to,
        title: link.to,
        label: labelText(link.toLabel),
        content: link.toContent ?? "",
        status: link.toStatus ?? defaultStatusForKind(link.toLabel ?? "")
    };
}

function buildDegreeMap(links: GraphLink[]) {
  const degreeById = new Map<string, number>();

  for (const link of links) {
    const fromId = link.fromId ?? link.from;
    const toId = link.toId ?? link.to;
    degreeById.set(fromId, (degreeById.get(fromId) ?? 0) + 1);
    degreeById.set(toId, (degreeById.get(toId) ?? 0) + 1);
  }

  return degreeById;
}

// All nodes within `maxDepth` hops of the selected node (BFS). Selecting a node lights up its
// whole local neighborhood out to 2 levels, not just its direct neighbors.
function buildNeighborIds(links: GraphLink[], selectedId?: string, maxDepth = 2) {
  const set = new Set<string>();
  if (!selectedId) {
    return set;
  }
  const adjacency = new Map<string, Set<string>>();
  const connect = (a: string, b: string) => {
    if (!adjacency.has(a)) {
      adjacency.set(a, new Set());
    }
    adjacency.get(a)!.add(b);
  };
  for (const link of links) {
    const fromId = link.fromId ?? link.from;
    const toId = link.toId ?? link.to;
    connect(fromId, toId);
    connect(toId, fromId);
  }
  const visited = new Set<string>([selectedId]);
  let frontier = new Set<string>([selectedId]);
  for (let depth = 0; depth < maxDepth && frontier.size > 0; depth += 1) {
    const next = new Set<string>();
    for (const id of frontier) {
      for (const neighbor of adjacency.get(id) ?? []) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          next.add(neighbor);
          set.add(neighbor);
        }
      }
    }
    frontier = next;
  }
  return set;
}

export function GardenPanel({
  metrics,
  links,
  sources,
  notes,
  relationTypes: editableRelationTypes = [],
  relationError,
  onUpdateSource,
  onUpdateRelation,
  onDeleteRelation,
  only
}: Props) {
  const [view, setView] = useState<"list" | "map">("list");
  const [relFilter, setRelFilter] = useState("all");
  const [editingRelationId, setEditingRelationId] = useState("");
  const [relationDrafts, setRelationDrafts] = useState<Record<string, string>>({});
  const [savingRelationId, setSavingRelationId] = useState("");
  const [deletingRelationId, setDeletingRelationId] = useState("");
  // Build the node set from ALL objects (sources + notes), not just relation endpoints, so
  // standalone notes/books also appear. Link endpoints are merged in to backfill any object
  // the loaders capped out of (and to carry the richer content/status from the graph query).
  const nodes = useMemo(() => {
    const map = new Map<string, GraphNode>();
    for (const source of sources ?? []) {
      mergeNode(map, nodeFromSource(source));
    }
    for (const note of notes ?? []) {
      mergeNode(map, nodeFromNote(note));
    }
    for (const link of links) {
      mergeNode(map, nodeFromLink(link, "from"));
      mergeNode(map, nodeFromLink(link, "to"));
    }
    return Array.from(map.values());
  }, [links, sources, notes]);
  const visibleRelationCount = useMemo(() => new Set(links.map((link) => link.label)).size, [links]);

  // Distinct relation types (belongs_to, mentions, …) for the Danh sách filter row.
  const relationTypes = useMemo(() => {
    const map = new Map<string, string>();
    for (const link of links) {
      if (!map.has(link.label)) {
        map.set(link.label, (link.labelName || link.label).replace(/_/g, " "));
      }
    }
    return Array.from(map, ([label, name]) => ({ label, name }));
  }, [links]);
  const filteredLinks = relFilter === "all" ? links : links.filter((link) => link.label === relFilter);
  const editableTypes = useMemo(
    () => editableRelationTypes.filter((type) => type.slug !== "contains"),
    [editableRelationTypes]
  );

  const startEditRelation = (link: GraphLink) => {
    if (!link.id) {
      return;
    }
    setEditingRelationId(link.id);
    setRelationDrafts((current) => ({ ...current, [link.id!]: link.label }));
  };

  const submitRelationEdit = async (link: GraphLink) => {
    if (!link.id || !onUpdateRelation) {
      return;
    }
    const nextType = relationDrafts[link.id] || link.label;
    setSavingRelationId(link.id);
    try {
      await onUpdateRelation(link.id, { relationType: nextType });
      setEditingRelationId("");
    } finally {
      setSavingRelationId("");
    }
  };

  const submitRelationDelete = async (link: GraphLink) => {
    if (!link.id || !onDeleteRelation) {
      return;
    }
    setDeletingRelationId(link.id);
    try {
      await onDeleteRelation(link.id);
      if (editingRelationId === link.id) {
        setEditingRelationId("");
      }
    } finally {
      setDeletingRelationId("");
    }
  };

  const relationList = (
    <div className="space-y-3 p-4">
      {links.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line bg-slate-50 px-3 py-4 text-sm font-medium text-muted">
          Chưa có liên kết nào để vẽ bản đồ.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <FilterChip active={relFilter === "all"} onClick={() => setRelFilter("all")}>
              Tất cả
            </FilterChip>
            {relationTypes.map((type) => (
              <FilterChip key={type.label} active={relFilter === type.label} onClick={() => setRelFilter(type.label)}>
                {type.name}
              </FilterChip>
            ))}
          </div>
          {filteredLinks.length === 0 ? (
            <p className="rounded-lg border border-dashed border-line bg-slate-50 px-3 py-4 text-sm font-medium text-muted">
              Không có liên kết loại này.
            </p>
          ) : (
            <div className="space-y-2">
              {filteredLinks.map((link) => (
                <article
                  key={link.id ?? `${link.from}-${link.to}-${link.label}`}
                  className="rounded-lg border border-line bg-white px-4 py-3 transition-colors hover:border-blue-200 hover:bg-slate-50"
                >
                  <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_minmax(0,1fr)] lg:items-center">
                    <GraphEndpoint node={nodeFromLink(link, "from")} tone="blue" />
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                      <ArrowRight size={14} className="shrink-0 text-muted" aria-hidden="true" />
                      <span className="max-w-full truncate rounded-md border border-line bg-slate-50 px-2 py-0.5" title={link.label}>
                        {relationText(link)}
                      </span>
                    </div>
                    <GraphEndpoint node={nodeFromLink(link, "to")} tone="green" />
                  </div>
                  <RelationEditControls
                    link={link}
                    relationTypes={editableTypes}
                    relationError={relationError}
                    editing={editingRelationId === link.id}
                    draft={link.id ? relationDrafts[link.id] || link.label : link.label}
                    saving={savingRelationId === link.id}
                    deleting={deletingRelationId === link.id}
                    onDraftChange={(value) => {
                      if (link.id) {
                        setRelationDrafts((current) => ({ ...current, [link.id!]: value }));
                      }
                    }}
                    onEdit={() => startEditRelation(link)}
                    onCancel={() => setEditingRelationId("")}
                    onSave={() => void submitRelationEdit(link)}
                    onDelete={() => void submitRelationDelete(link)}
                  />
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  // Single-view modes (Library sub-tabs).
  if (only === "kanban") {
    return sources && onUpdateSource ? (
      <section className="rounded-lg border border-line bg-white p-4 shadow-panel">
        <StatusBoard sources={sources} onUpdateSource={onUpdateSource} />
      </section>
    ) : null;
  }
  if (only === "list") {
    return <section className="rounded-lg border border-line bg-white shadow-panel">{relationList}</section>;
  }
  if (only === "map") {
    return (
      <section className="rounded-lg border border-line bg-white shadow-panel">
        <GraphMap
          links={links}
          nodes={nodes}
          sources={sources}
          relationTypes={editableTypes}
          relationError={relationError}
          onUpdateSource={onUpdateSource}
          onUpdateRelation={onUpdateRelation}
          onDeleteRelation={onDeleteRelation}
          forceMode="obsidian"
        />
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-line bg-white shadow-panel">
      <div className="border-b border-line px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold">Bản đồ kiến thức</h2>
            <p className="text-sm font-medium text-muted">Nhìn các node và liên kết trong không gian của bạn.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md border border-line bg-slate-50 px-3 py-2 text-right text-xs font-bold text-muted">
              {nodes.length} node · {visibleRelationCount} loại liên kết
            </span>
            <div className="inline-flex rounded-lg border border-line bg-white p-1">
              <button
                className={`inline-flex h-8 items-center gap-2 rounded-md px-3 text-xs font-bold ${
                  view === "list" ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
                }`}
                type="button"
                onClick={() => setView("list")}
              >
                <List size={14} aria-hidden="true" />
                Danh sách
              </button>
              <button
                className={`inline-flex h-8 items-center gap-2 rounded-md px-3 text-xs font-bold ${
                  view === "map" ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
                }`}
                type="button"
                onClick={() => setView("map")}
              >
                <Network size={14} aria-hidden="true" />
                Bản đồ
              </button>
            </div>
          </div>
        </div>
      </div>

      {view === "list" ? relationList : (
        <GraphMap
          links={links}
          nodes={nodes}
          sources={sources}
          relationTypes={editableTypes}
          relationError={relationError}
          onUpdateSource={onUpdateSource}
          onUpdateRelation={onUpdateRelation}
          onDeleteRelation={onDeleteRelation}
        />
      )}
    </section>
  );
}

function relationTypeLabel(type: RelationType) {
  return type.nameVi || type.name || type.slug.replace(/_/g, " ");
}

function RelationEditControls({
  link,
  relationTypes,
  relationError,
  editing,
  draft,
  saving,
  deleting,
  onDraftChange,
  onEdit,
  onCancel,
  onSave,
  onDelete
}: {
  link: GraphLink;
  relationTypes: RelationType[];
  relationError?: string;
  editing: boolean;
  draft: string;
  saving: boolean;
  deleting: boolean;
  onDraftChange: (value: string) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
}) {
  const deleteConfirm = useSecondClickConfirm();
  const confirmId = link.id ?? `${link.from}-${link.to}-${link.label}`;

  if (!link.id || relationTypes.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3">
      {editing ? (
        <>
          <select
            className="h-9 min-w-[180px] rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            aria-label="Đổi loại relation"
          >
            {relationTypes.map((type) => (
              <option key={type.id} value={type.slug}>
                {relationTypeLabel(type)}
              </option>
            ))}
          </select>
          <button
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
            type="button"
            disabled={saving || deleting || draft === link.label}
            onClick={onSave}
          >
            {saving ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Pencil size={14} aria-hidden="true" />}
            Đổi loại
          </button>
          <button
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-600 hover:bg-slate-50"
            type="button"
            disabled={saving || deleting}
            onClick={onCancel}
          >
            <X size={14} aria-hidden="true" />
            Hủy
          </button>
        </>
      ) : (
        <button
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-600 hover:bg-slate-50"
          type="button"
          disabled={deleting}
          onClick={onEdit}
        >
          <Pencil size={14} aria-hidden="true" />
          Đổi loại
        </button>
      )}
      <button
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 text-xs font-bold text-red-700 hover:bg-red-100 disabled:bg-slate-100 disabled:text-slate-400"
        type="button"
        disabled={saving || deleting}
        onClick={() => deleteConfirm.request(confirmId, onDelete)}
      >
        {deleting ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Trash2 size={14} aria-hidden="true" />}
        {deleteConfirm.pendingId === confirmId ? "Bấm lần nữa để xóa" : "Xóa"}
      </button>
      {relationError ? <p className="basis-full text-right text-xs font-semibold text-red-700">{relationError}</p> : null}
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-8 items-center rounded-lg border px-3 text-sm font-bold ${
        active ? "border-blue-200 bg-blue-50 text-brand" : "border-line bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function GraphEndpoint({ node, tone }: { node: GraphNode; tone: "blue" | "green" }) {
  const secondary = nodeSecondaryText(node);
  const toneClasses =
    tone === "blue"
      ? "border-blue-100 bg-blue-50 text-brand"
      : "border-green-100 bg-green-50 text-leaf";

  return (
    <div className="flex min-w-0 items-start gap-2">
      <span className={`shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${toneClasses}`}>
        {node.label}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-ink" title={plainText(node.content) || node.title}>
          {nodePrimaryText(node, 100)}
        </p>
        {secondary ? <p className="mt-0.5 truncate text-xs font-medium text-muted">{secondary}</p> : null}
      </div>
    </div>
  );
}

function GraphMap({
  links,
  nodes,
  sources,
  relationTypes = [],
  relationError,
  onUpdateSource,
  onUpdateRelation,
  onDeleteRelation,
  forceMode
}: {
  links: GraphLink[];
  nodes: GraphNode[];
  sources?: SourceNode[];
  relationTypes?: RelationType[];
  relationError?: string;
  onUpdateSource?: UpdateSource;
  onUpdateRelation?: (id: string, input: { relationType: string }) => Promise<void>;
  onDeleteRelation?: (id: string) => Promise<void>;
  forceMode?: "cards" | "obsidian" | "kanban";
}) {
  // No node selected by default → detail panel is hidden until the user clicks a node.
  const [selectedId, setSelectedId] = useState("");
  const [mapMode, setMapMode] = useState<"cards" | "obsidian" | "kanban">(forceMode ?? "obsidian");
  const [editingRelationId, setEditingRelationId] = useState("");
  const [relationDrafts, setRelationDrafts] = useState<Record<string, string>>({});
  const [savingRelationId, setSavingRelationId] = useState("");
  const [deletingRelationId, setDeletingRelationId] = useState("");
  // How many nodes to draw. Default 30 keeps the graph readable; user can raise it.
  const [nodeLimit, setNodeLimit] = useState(DEFAULT_NODE_LIMIT);
  const canKanban = Boolean(sources && onUpdateSource);
  const selectedNode = nodes.find((node) => node.id === selectedId);

  // Click a node to open its detail panel; click the same node again to close it.
  const toggleSelect = (id: string) => {
    setSelectedId((current) => (current === id ? "" : id));
  };

  // Degree over ALL links — used to pick the most-connected nodes when the limit bites.
  const degreeById = useMemo(() => buildDegreeMap(links), [links]);

  // Nodes within 2 hops of the selected node (over the FULL graph), so clicking a node lights up
  // its local neighborhood out to 2 levels — and those nodes are force-kept visible below.
  const neighborIds = useMemo(() => buildNeighborIds(links, selectedId), [links, selectedId]);

  // Visible node set = top-N by degree, plus the selected node and its full 2-hop neighborhood.
  const visibleNodes = useMemo(() => {
    const sorted = [...nodes].sort((a, b) => (degreeById.get(b.id) ?? 0) - (degreeById.get(a.id) ?? 0));
    const top = sorted.slice(0, nodeLimit);
    const included = new Set(top.map((node) => node.id));
    const keep = (id: string) => {
      if (id && !included.has(id)) {
        const node = nodes.find((item) => item.id === id);
        if (node) {
          top.push(node);
          included.add(id);
        }
      }
    };
    keep(selectedId);
    neighborIds.forEach(keep);
    return top;
  }, [nodes, degreeById, nodeLimit, selectedId, neighborIds]);

  const visibleIds = useMemo(() => new Set(visibleNodes.map((node) => node.id)), [visibleNodes]);
  // Only draw edges whose both endpoints are visible, so no dangling links.
  const visibleLinks = useMemo(
    () => links.filter((link) => visibleIds.has(link.fromId ?? link.from) && visibleIds.has(link.toId ?? link.to)),
    [links, visibleIds]
  );
  const visibleDegreeById = useMemo(() => buildDegreeMap(visibleLinks), [visibleLinks]);

  // Detail panel shows ALL relations of the selected node, even to hidden nodes.
  const relatedLinks = selectedNode
    ? links.filter((link) => (link.fromId ?? link.from) === selectedNode.id || (link.toId ?? link.to) === selectedNode.id)
    : [];
  const cardPositions = useMemo(() => buildCardPositions(visibleNodes, visibleDegreeById), [visibleDegreeById, visibleNodes]);

  const startEditRelation = (link: GraphLink) => {
    if (!link.id) {
      return;
    }
    setEditingRelationId(link.id);
    setRelationDrafts((current) => ({ ...current, [link.id!]: link.label }));
  };

  const submitRelationEdit = async (link: GraphLink) => {
    if (!link.id || !onUpdateRelation) {
      return;
    }
    const nextType = relationDrafts[link.id] || link.label;
    setSavingRelationId(link.id);
    try {
      await onUpdateRelation(link.id, { relationType: nextType });
      setEditingRelationId("");
    } finally {
      setSavingRelationId("");
    }
  };

  const submitRelationDelete = async (link: GraphLink) => {
    if (!link.id || !onDeleteRelation) {
      return;
    }
    setDeletingRelationId(link.id);
    try {
      await onDeleteRelation(link.id);
      if (editingRelationId === link.id) {
        setEditingRelationId("");
      }
    } finally {
      setDeletingRelationId("");
    }
  };

  useEffect(() => {
    // If the selected node disappears (data refresh), just clear it — don't auto-select another.
    if (selectedId && !nodes.some((node) => node.id === selectedId)) {
      setSelectedId("");
    }
  }, [nodes, selectedId]);

  if (nodes.length === 0) {
    return (
      <div className="p-4">
        <p className="rounded-lg border border-dashed border-line bg-slate-50 px-3 py-4 text-sm font-medium text-muted">
          Chưa có nút nào để vẽ bản đồ.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className={`inline-flex rounded-lg border border-line bg-white p-1 ${forceMode ? "hidden" : ""}`}>
          <button
            className={`inline-flex h-8 items-center gap-2 rounded-md px-3 text-xs font-bold ${
              mapMode === "cards" ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
            }`}
            type="button"
            onClick={() => setMapMode("cards")}
          >
            Cards
          </button>
          <button
            className={`inline-flex h-8 items-center gap-2 rounded-md px-3 text-xs font-bold ${
              mapMode === "obsidian" ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
            }`}
            type="button"
            onClick={() => setMapMode("obsidian")}
          >
            Obsidian
          </button>
          {canKanban ? (
            <button
              className={`inline-flex h-8 items-center gap-2 rounded-md px-3 text-xs font-bold ${
                mapMode === "kanban" ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
              }`}
              type="button"
              onClick={() => setMapMode("kanban")}
            >
              Kanban
            </button>
          ) : null}
        </div>
        {mapMode === "kanban" ? (
          <p className="text-xs font-semibold text-muted">Bảng trạng thái nguồn, kéo thẻ hoặc đổi dropdown để chuyển cột.</p>
        ) : (
          <div className="flex items-center gap-2 text-xs font-semibold text-muted">
            <span>Số node:</span>
            <div className="inline-flex rounded-lg border border-line bg-white p-1">
              {NODE_LIMIT_PRESETS.map((preset) => (
                <button
                  key={preset}
                  className={`inline-flex h-7 items-center rounded-md px-2.5 ${
                    nodeLimit === preset ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
                  }`}
                  type="button"
                  onClick={() => setNodeLimit(preset)}
                >
                  {preset}
                </button>
              ))}
              <button
                className={`inline-flex h-7 items-center rounded-md px-2.5 ${
                  nodeLimit >= nodes.length ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
                }`}
                type="button"
                onClick={() => setNodeLimit(nodes.length)}
              >
                Tất cả ({nodes.length})
              </button>
            </div>
            <span className="text-muted">· hiện {visibleNodes.length}</span>
          </div>
        )}
      </div>

      <StatusLegend />

      {mapMode === "kanban" && sources && onUpdateSource ? (
        <StatusBoard sources={sources} onUpdateSource={onUpdateSource} />
      ) : (
        <div className={selectedNode ? "grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]" : "grid gap-4"}>
          {mapMode === "cards" ? (
            <CardGraphCanvas
              links={visibleLinks}
              nodes={visibleNodes}
              positions={cardPositions}
              selectedNode={selectedNode}
              neighborIds={neighborIds}
              onSelect={toggleSelect}
            />
          ) : (
            <Suspense
              fallback={
                <div className="flex min-h-[440px] items-center justify-center rounded-lg border border-line bg-white text-sm font-semibold text-muted sm:min-h-[620px]">
                  Đang tải graph động...
                </div>
              }
            >
              <ObsidianForceGraph
                degreeById={visibleDegreeById}
                links={visibleLinks}
                nodes={visibleNodes}
                selectedNode={selectedNode}
                neighborIds={neighborIds}
                onSelect={toggleSelect}
              />
            </Suspense>
          )}

          {selectedNode ? (
            <GraphDetailPanel
              links={relatedLinks}
              relationTypes={relationTypes}
              relationError={relationError}
              selectedNode={selectedNode}
              editingRelationId={editingRelationId}
              relationDrafts={relationDrafts}
              savingRelationId={savingRelationId}
              deletingRelationId={deletingRelationId}
              onSelect={toggleSelect}
              onDraftChange={(id, value) => setRelationDrafts((current) => ({ ...current, [id]: value }))}
              onEditRelation={startEditRelation}
              onCancelEdit={() => setEditingRelationId("")}
              onSaveRelation={(link) => void submitRelationEdit(link)}
              onDeleteRelation={(link) => void submitRelationDelete(link)}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function StatusLegend() {
  const entries = useMemo(() => {
    const seen = new Map<string, { label: string; dotColor: string }>();
    for (const group of Object.values(LIFECYCLE_OPTIONS)) {
      for (const option of group) {
        if (!seen.has(option.label)) {
          seen.set(option.label, { label: option.label, dotColor: option.dotColor });
        }
      }
    }
    return Array.from(seen.values());
  }, []);

  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-line bg-slate-50 px-3 py-2">
      <span className="text-[11px] font-bold uppercase tracking-wide text-muted">Trạng thái</span>
      {entries.map((entry) => (
        <span key={entry.label} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.dotColor }} aria-hidden="true" />
          {entry.label}
        </span>
      ))}
    </div>
  );
}

function buildCardPositions(nodes: GraphNode[], degreeById: Map<string, number>) {
  const positions = new Map<string, NodePosition>();
  const centerX = 50;
  const centerY = 50;
  const radiusX = 38;
  const radiusY = 32;
  const centerNode = [...nodes].sort((left, right) => (degreeById.get(right.id) ?? 0) - (degreeById.get(left.id) ?? 0))[0];
  const ringNodes = centerNode ? nodes.filter((node) => node.id !== centerNode.id) : nodes;

  if (centerNode) {
    positions.set(centerNode.id, { x: centerX, y: centerY });
  }

  ringNodes.forEach((node, index) => {
    const angle = (Math.PI * 2 * index) / Math.max(ringNodes.length, 1) - Math.PI / 2;
    positions.set(node.id, {
      x: centerX + Math.cos(angle) * radiusX,
      y: centerY + Math.sin(angle) * radiusY
    });
  });

  return positions;
}

function CardGraphCanvas({
  links,
  nodes,
  positions,
  selectedNode,
  neighborIds,
  onSelect
}: {
  links: GraphLink[];
  nodes: GraphNode[];
  positions: Map<string, NodePosition>;
  selectedNode?: GraphNode;
  neighborIds: Set<string>;
  onSelect: (id: string) => void;
}) {
  const hasSelection = Boolean(selectedNode);

  return (
    <div className="relative min-h-[440px] overflow-hidden rounded-lg border border-line bg-slate-50 sm:min-h-[620px]">
      <GraphLines links={links} positions={positions} selectedNode={selectedNode} showLabels />

      {nodes.map((node) => {
        const position = positions.get(node.id);
        const selected = selectedNode?.id === node.id;
        const neighbor = neighborIds.has(node.id);
        const noteNode = isNoteLike(node.label);
        const dimmed = hasSelection && !selected && !neighbor;

        if (!position) {
          return null;
        }

        return (
          <button
            key={node.id}
            className={`absolute w-44 max-w-[42vw] rounded-lg border p-3 text-left shadow-sm transition-all ${
              selected
                ? "z-20 border-blue-400 bg-blue-50 ring-2 ring-blue-400 ring-offset-1"
                : neighbor
                  ? "z-10 border-blue-300 bg-blue-50/70 ring-2 ring-blue-200"
                  : noteNode
                    ? "border-blue-100 bg-white hover:border-blue-200 hover:bg-blue-50/60"
                    : "border-green-100 bg-white hover:border-green-200 hover:bg-green-50/60"
            } ${dimmed ? "opacity-45" : "opacity-100"}`}
            style={{
              left: `${position.x}%`,
              top: `${position.y}%`,
              transform: "translate(-50%, -50%)"
            }}
            type="button"
            onClick={() => onSelect(node.id)}
            title={plainText(node.content) || node.title}
          >
            <div className="mb-1 flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: statusColor(node.label, node.status) }}
                title={statusMeta(node.label, node.status).label}
                aria-hidden="true"
              />
              <GitBranch size={14} className={`shrink-0 ${noteNode ? "text-brand" : "text-leaf"}`} aria-hidden="true" />
              <span className={`truncate text-[11px] font-bold uppercase tracking-wide ${noteNode ? "text-brand" : "text-leaf"}`}>
                {node.label}
              </span>
            </div>
            <p className="line-clamp-2 text-xs font-bold leading-5 text-ink">{nodePrimaryText(node, 64)}</p>
          </button>
        );
      })}
    </div>
  );
}

function GraphLines({
  links,
  positions,
  selectedNode,
  showLabels = false
}: {
  links: GraphLink[];
  positions: Map<string, NodePosition>;
  selectedNode?: GraphNode;
  showLabels?: boolean;
}) {
  return (
    <>
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {links.map((link) => {
          const from = positions.get(link.fromId ?? link.from);
          const to = positions.get(link.toId ?? link.to);
          const active = selectedNode ? (link.fromId ?? link.from) === selectedNode.id || (link.toId ?? link.to) === selectedNode.id : false;

          if (!from || !to) {
            return null;
          }

          return (
            <line
              key={link.id ?? `${link.from}-${link.to}-${link.label}`}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke={active ? "#7c3aed" : "#d7dde6"}
              strokeOpacity={active ? 0.95 : 0.45}
              strokeWidth={active ? "0.45" : "0.22"}
            />
          );
        })}
      </svg>

      {showLabels
        ? links.map((link) => {
            const from = positions.get(link.fromId ?? link.from);
            const to = positions.get(link.toId ?? link.to);
            const active = selectedNode ? (link.fromId ?? link.from) === selectedNode.id || (link.toId ?? link.to) === selectedNode.id : false;

            if (!from || !to) {
              return null;
            }

            return (
              <span
                key={`label-${link.id ?? `${link.from}-${link.to}-${link.label}`}`}
                className={`absolute max-w-28 truncate rounded-md border px-2 py-1 text-[11px] font-bold shadow-sm ${
                  active ? "border-blue-100 bg-blue-50 text-brand" : "border-line bg-white text-slate-600"
                }`}
                style={{
                  left: `${(from.x + to.x) / 2}%`,
                  top: `${(from.y + to.y) / 2}%`,
                  transform: "translate(-50%, -50%)"
                }}
              >
                {relationText(link)}
              </span>
            );
          })
        : null}
    </>
  );
}

function GraphDetailPanel({
  links,
  relationTypes,
  relationError,
  selectedNode,
  editingRelationId,
  relationDrafts,
  savingRelationId,
  deletingRelationId,
  onSelect,
  onDraftChange,
  onEditRelation,
  onCancelEdit,
  onSaveRelation,
  onDeleteRelation
}: {
  links: GraphLink[];
  relationTypes: RelationType[];
  relationError?: string;
  selectedNode?: GraphNode;
  editingRelationId: string;
  relationDrafts: Record<string, string>;
  savingRelationId: string;
  deletingRelationId: string;
  onSelect: (id: string) => void;
  onDraftChange: (id: string, value: string) => void;
  onEditRelation: (link: GraphLink) => void;
  onCancelEdit: () => void;
  onSaveRelation: (link: GraphLink) => void;
  onDeleteRelation: (link: GraphLink) => void;
}) {
  return (
    <aside className="rounded-lg border border-line bg-white p-4">
      {selectedNode ? (
        <div>
          <div className="mb-3 inline-flex rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-brand">
            {selectedNode.label}
          </div>
          <h3 className="text-base font-bold leading-6 text-ink">{nodePrimaryText(selectedNode, 160)}</h3>
          {nodeSecondaryText(selectedNode) ? <p className="mt-2 text-sm font-semibold text-muted">{nodeSecondaryText(selectedNode)}</p> : null}
          {plainText(selectedNode.content) ? (
            <p className="mt-3 text-sm leading-6 text-slate-700">{shortTitle(plainText(selectedNode.content), 420)}</p>
          ) : null}

          <div className="mt-5 border-t border-line pt-4">
            <h4 className="text-xs font-bold uppercase tracking-wide text-muted">Liên kết</h4>
            <div className="mt-3 space-y-2">
              {links.map((link) => {
                const selectedIsSource = (link.fromId ?? link.from) === selectedNode.id;
                const neighbor = nodeFromLink(link, selectedIsSource ? "to" : "from");

                return (
                  <div
                    key={`detail-${link.id ?? `${link.from}-${link.to}-${link.label}`}`}
                    className="rounded-lg border border-line bg-slate-50 px-3 py-2 transition-colors hover:border-blue-200 hover:bg-blue-50"
                  >
                    <button className="block w-full text-left" type="button" onClick={() => onSelect(neighbor.id)}>
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                        <span>{selectedIsSource ? "to" : "from"}</span>
                        <span className="truncate rounded-md border border-line bg-white px-2 py-0.5">{relationText(link)}</span>
                      </div>
                      <p className="mt-1 truncate text-sm font-bold text-ink">{nodePrimaryText(neighbor, 80)}</p>
                    </button>
                    <RelationEditControls
                      link={link}
                      relationTypes={relationTypes}
                      relationError={relationError}
                      editing={editingRelationId === link.id}
                      draft={link.id ? relationDrafts[link.id] || link.label : link.label}
                      saving={savingRelationId === link.id}
                      deleting={deletingRelationId === link.id}
                      onDraftChange={(value) => {
                        if (link.id) {
                          onDraftChange(link.id, value);
                        }
                      }}
                      onEdit={() => onEditRelation(link)}
                      onCancel={onCancelEdit}
                      onSave={() => onSaveRelation(link)}
                      onDelete={() => onDeleteRelation(link)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
